import { concat, uniqBy } from 'lodash';
import { useMemo } from 'react';

import type { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import type { AIOnboardingModalEntityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import type { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import type { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  isKGV2EntityPrincipalNodeType,
  isKGV2PersonPrincipalNodeType,
  type KGV2NodeForGrouping,
  type ResolvedPrincipalFromKGV2,
  resolvePrincipalsFromNodeMap,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { useHouseholdCashflowEntityOptionsQuery } from '@/modules/cashflows/CashflowDrawer/graphql/HouseholdCashflowEntityOptions.generated';
import type { ClientProfileNameFields } from '@/modules/clientProfiles/ClientProfile.types';
import {
  ALLOW_MULTIPLE_PRINCIPALS_TYPES_WITH_BUSINESS_ENTITIES,
  BUSINESS_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import type { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { EntityStage } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

export interface CreatableOwnerEntityOption extends SelectInputOption<string> {
  nodeName: string;
  suggestionID?: string;
  entityType?: EntityType | '';
}

export interface CreatablePrincipalPersonOption
  extends SelectInputOption<string> {
  nodeName: string;
  suggestionID?: string;
  initialClientProfileName: ClientProfileNameFields;
}

interface UseOnboardingEntityPrincipalOptionsProps {
  possiblePrimaryClients: AiOnboardingModal_HouseholdFragment['possiblePrimaryClients'];
  possibleGrantors: AiOnboardingModal_HouseholdFragment['possibleGrantors'];
  suggestionsByID: Record<string, AiSuggestionForReviewCard>;
  suggestionID: string | undefined;
  entityType: EntityType | '';
  entityFieldValues: AIOnboardingModalEntityField[];
  /** Household ID for fetching existing entity options */
  householdId?: string;
  /**
   * KG V2 nodes keyed by ID, used to resolve this entity's principals directly
   * from `principalNodeIDs`.
   */
  principalNodesById?: Map<string, KGV2NodeForGrouping>;
}

interface UseOnboardingEntityPrincipalOptionsReturn {
  /**
   * Options for the entity's principal(s) that are already created as a
   * Client Profile.
   */
  clientProfileOptions: SelectInputOption<string>[];
  /**
   * Options for the entity's suggested principal(s) that are not yet created
   * as a Client Profile.
   */
  suggestedPrincipalOptions: CreatablePrincipalPersonOption[];
  /**
   * Options for the entity's suggested principal(s) that are other entity
   * suggestions (not created as an Entity yet).
   */
  businessEntityOwnerEntityOptions: SelectInputOption<string>[];
  /**
   * Options for entity owner nodes that must be created before this entity can
   * use them as owners. These include owner nodes represented by additional
   * reference suggestions that are not visible rows in the current modal.
   */
  creatableOwnerEntityOptions: CreatableOwnerEntityOption[];
  /**
   * Whether the entity type allows multiple principals.
   */
  allowMultiplePrincipals: boolean;
  /** Existing household entities to include as selectable principals */
  existingEntityOptions: SelectInputOption<string>[];
  /** KGV2 principals from the onboarding entity suggestion. */
  kgV2ResolvedPrincipals: ResolvedPrincipalFromKGV2[];
}

function getOnboardingEntityDisplayName(
  suggestion: AiSuggestionForReviewCard
): string | undefined {
  const onboardingEntity = suggestion.onboardingEntity;
  if (!onboardingEntity) {
    return undefined;
  }

  return (
    onboardingEntity.displayName ||
    (onboardingEntity.entityName
      ? sentenceCaseWithAcronyms(onboardingEntity.entityName)
      : sentenceCaseWithAcronyms(onboardingEntity.nodeName))
  );
}

function getClientProfileNameFromNodeName(
  nodeName: string
): ClientProfileNameFields {
  const nameParts = sentenceCaseWithAcronyms(nodeName)
    .split(/\s+/)
    .filter(Boolean);

  if (nameParts.length <= 1) {
    return {
      firstName: nameParts[0] || '',
      middleName: '',
      lastName: '',
      suffix: '',
    };
  }

  const [firstName = '', ...remainingNameParts] = nameParts;
  const lastName = remainingNameParts.pop() || '';

  return {
    firstName,
    middleName: remainingNameParts.join(' '),
    lastName,
    suffix: '',
  };
}

function getDisplayNameFromClientProfileName({
  firstName,
  middleName,
  lastName,
  suffix,
}: ClientProfileNameFields): string {
  return [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');
}

function getOptionDisplayKey(display: string): string {
  return display.trim().replace(/\s+/g, ' ').toLowerCase();
}

function dedupeCreatableOptionsByDisplay<
  T extends SelectInputOption<string> & { suggestionID?: string },
>(options: T[]): T[] {
  const optionsByDisplay = new Map<string, T>();

  options.forEach((option) => {
    const displayKey = getOptionDisplayKey(option.display);
    if (!displayKey) {
      return;
    }

    const existingOption = optionsByDisplay.get(displayKey);
    if (
      !existingOption ||
      (!existingOption.suggestionID && option.suggestionID)
    ) {
      optionsByDisplay.set(displayKey, option);
    }
  });

  return Array.from(optionsByDisplay.values());
}

export function excludeExistingEntityOptionsAlreadyIncludedAsOwnerOptions({
  businessEntityOwnerEntityOptions,
  existingEntityOptions,
}: {
  businessEntityOwnerEntityOptions: SelectInputOption<string>[];
  existingEntityOptions: SelectInputOption<string>[];
}): SelectInputOption<string>[] {
  const ownerEntityOptionValues = new Set(
    businessEntityOwnerEntityOptions.map((o) => o.value)
  );

  return existingEntityOptions.filter(
    (option) => !ownerEntityOptionValues.has(option.value)
  );
}

export function getKGV2SuggestedPrincipalOptions({
  kgV2ResolvedPrincipals,
  suggestionsByID,
}: {
  kgV2ResolvedPrincipals: ResolvedPrincipalFromKGV2[];
  suggestionsByID: Record<string, AiSuggestionForReviewCard>;
}): CreatablePrincipalPersonOption[] {
  return dedupeCreatableOptionsByDisplay(
    kgV2ResolvedPrincipals
      .filter((principal) => !principal.isResolved)
      .filter((principal) => isKGV2PersonPrincipalNodeType(principal.nodeType))
      .map((principal): CreatablePrincipalPersonOption | null => {
        if (!principal.nodeName) {
          return null;
        }

        const individualSuggestion = principal.referenceSuggestionID
          ? suggestionsByID[principal.referenceSuggestionID]
          : undefined;
        const onboardingIndividual = individualSuggestion?.onboardingIndividual;

        if (onboardingIndividual) {
          const initialClientProfileName = {
            firstName: onboardingIndividual.firstName ?? '',
            middleName: onboardingIndividual.middleName ?? '',
            lastName: onboardingIndividual.lastName ?? '',
            suffix: onboardingIndividual.suffix ?? '',
          };
          const displayName = getDisplayNameFromClientProfileName(
            initialClientProfileName
          );

          if (!displayName) {
            diagnostics.error(
              '[useOnboardingEntityPrincipalOptions][getKGV2SuggestedPrincipalOptions] - WARNING: empty displayName of OnboardingEntity suggested principal'
            );
            return null;
          }

          return {
            display: displayName,
            value: individualSuggestion.id,
            nodeName: principal.nodeName,
            suggestionID: individualSuggestion.id,
            initialClientProfileName,
          };
        }

        const initialClientProfileName = getClientProfileNameFromNodeName(
          principal.nodeName
        );
        const displayName = getDisplayNameFromClientProfileName(
          initialClientProfileName
        );

        if (!displayName) {
          return null;
        }

        return {
          display: displayName,
          value: principal.nodeId,
          nodeName: principal.nodeName,
          initialClientProfileName,
        };
      })
      .filter((o): o is CreatablePrincipalPersonOption => Boolean(o))
  );
}

function getKGV2EntityOwnerOptionDisplayName({
  principal,
  suggestion,
}: {
  principal: ResolvedPrincipalFromKGV2;
  suggestion: AiSuggestionForReviewCard | undefined;
}): string {
  return (
    suggestion?.onboardedEntity?.subtype?.displayName ||
    (suggestion && getOnboardingEntityDisplayName(suggestion)) ||
    principal.resolvedDisplayName ||
    sentenceCaseWithAcronyms(principal.nodeName)
  );
}

function getKGV2EntityOwnerOptionContext({
  entityFieldValues,
  kgV2ResolvedPrincipals,
  suggestionID,
  suggestionsByID,
}: Pick<
  UseOnboardingEntityPrincipalOptionsProps,
  'entityFieldValues' | 'suggestionID' | 'suggestionsByID'
> & {
  kgV2ResolvedPrincipals: ResolvedPrincipalFromKGV2[];
}) {
  const entityPrincipalNodes = kgV2ResolvedPrincipals.filter((principal) =>
    isKGV2EntityPrincipalNodeType(principal.nodeType)
  );
  const formRowBySuggestionID = new Map(
    entityFieldValues
      .filter((f) => f._suggestionID)
      .filter((f) => f._suggestionID !== suggestionID)
      .map((f) => [f._suggestionID!, f])
  );

  const getReferenceSuggestion = (principal: ResolvedPrincipalFromKGV2) =>
    principal.referenceSuggestionID
      ? suggestionsByID[principal.referenceSuggestionID]
      : undefined;

  return {
    entityPrincipalNodes,
    formRowBySuggestionID,
    getReferenceSuggestion,
  };
}

export function getKGV2BusinessEntityOwnerEntityOptions({
  entityFieldValues,
  entityType,
  kgV2ResolvedPrincipals,
  suggestionID,
  suggestionsByID,
}: Pick<
  UseOnboardingEntityPrincipalOptionsProps,
  'entityFieldValues' | 'entityType' | 'suggestionID' | 'suggestionsByID'
> & {
  kgV2ResolvedPrincipals: ResolvedPrincipalFromKGV2[];
}): SelectInputOption<string>[] {
  if (!BUSINESS_ENTITY_TYPES.includes(entityType as EntityType)) {
    return [];
  }

  const {
    entityPrincipalNodes,
    formRowBySuggestionID,
    getReferenceSuggestion,
  } = getKGV2EntityOwnerOptionContext({
    entityFieldValues,
    kgV2ResolvedPrincipals,
    suggestionID,
    suggestionsByID,
  });

  return uniqBy(
    entityPrincipalNodes
      .map((principal): SelectInputOption<string> | null => {
        const referenceSuggestion = getReferenceSuggestion(principal);
        const formRow = principal.referenceSuggestionID
          ? formRowBySuggestionID.get(principal.referenceSuggestionID)
          : undefined;
        const resolvedEntityID =
          principal.resolvedId || referenceSuggestion?.onboardedEntity?.id;

        if (formRow) {
          return {
            display:
              referenceSuggestion?.onboardedEntity?.subtype?.displayName ||
              formRow.entityName,
            value: resolvedEntityID || formRow._suggestionID!,
          };
        }

        if (!resolvedEntityID) {
          return null;
        }

        return {
          display: getKGV2EntityOwnerOptionDisplayName({
            principal,
            suggestion: referenceSuggestion,
          }),
          value: resolvedEntityID,
        };
      })
      .filter((o): o is SelectInputOption<string> => Boolean(o)),
    'value'
  );
}

export function getKGV2CreatableOwnerEntityOptions({
  entityFieldValues,
  entityType,
  kgV2ResolvedPrincipals,
  suggestionID,
  suggestionsByID,
}: Pick<
  UseOnboardingEntityPrincipalOptionsProps,
  'entityFieldValues' | 'entityType' | 'suggestionID' | 'suggestionsByID'
> & {
  kgV2ResolvedPrincipals: ResolvedPrincipalFromKGV2[];
}): CreatableOwnerEntityOption[] {
  if (!BUSINESS_ENTITY_TYPES.includes(entityType as EntityType)) {
    return [];
  }

  const {
    entityPrincipalNodes,
    formRowBySuggestionID,
    getReferenceSuggestion,
  } = getKGV2EntityOwnerOptionContext({
    entityFieldValues,
    kgV2ResolvedPrincipals,
    suggestionID,
    suggestionsByID,
  });

  return dedupeCreatableOptionsByDisplay(
    uniqBy(
      entityPrincipalNodes
        .map((principal): CreatableOwnerEntityOption | null => {
          const referenceSuggestion = getReferenceSuggestion(principal);

          if (
            principal.resolvedId ||
            referenceSuggestion?.onboardedEntity?.id ||
            (principal.referenceSuggestionID &&
              formRowBySuggestionID.has(principal.referenceSuggestionID))
          ) {
            return null;
          }

          if (!principal.nodeName) {
            return null;
          }

          const option: CreatableOwnerEntityOption = {
            display: getKGV2EntityOwnerOptionDisplayName({
              principal,
              suggestion: referenceSuggestion,
            }),
            value: referenceSuggestion?.id || principal.nodeId,
            nodeName: principal.nodeName,
            entityType: referenceSuggestion?.onboardingEntity?.entityKind
              ? getEntityTypeFromEntityKind(
                  referenceSuggestion.onboardingEntity.entityKind
                )
              : '',
          };

          if (referenceSuggestion?.id) {
            option.suggestionID = referenceSuggestion.id;
          }

          return option;
        })
        .filter((o): o is CreatableOwnerEntityOption => Boolean(o)),
      'nodeName'
    )
  );
}

/**
 * Returns the grantor/owners/donors options for the suggested entity to onboard.
 *
 * Primary clients and Client Profiles marked as "isGrantor" (created from the
 * previous Create Individuals & Organizations step) will always be included.
 *
 * If the suggested entity is a business entity, we also need to include
 * options that are other suggested onboarding entities.
 *
 * @param possiblePrimaryClients
 * @param possibleGrantors
 * @param suggestionsByID
 * @param suggestionID
 * @param entityType
 * @param entityFieldValues
 * @param householdId
 */
export function useOnboardingEntityPrincipalOptions({
  possiblePrimaryClients,
  possibleGrantors,
  suggestionsByID,
  suggestionID,
  entityType,
  entityFieldValues,
  householdId,
  principalNodesById,
}: UseOnboardingEntityPrincipalOptionsProps): UseOnboardingEntityPrincipalOptionsReturn {
  const kgV2ResolvedPrincipals = useMemo<ResolvedPrincipalFromKGV2[]>(() => {
    if (!suggestionID) {
      return [];
    }

    const suggestion = suggestionsByID[suggestionID];
    if (!suggestion) {
      return [];
    }

    return resolvePrincipalsFromNodeMap(
      suggestion.onboardingEntity?.principalNodeIDs ?? [],
      principalNodesById ?? new Map()
    );
  }, [suggestionID, suggestionsByID, principalNodesById]);

  const clientProfileOptions = useMemo(
    () =>
      uniqBy([...possibleGrantors, ...possiblePrimaryClients], 'id').map(
        (client) => ({
          display: client.displayName,
          value: client.id,
        })
      ),
    [possibleGrantors, possiblePrimaryClients]
  );

  const businessEntityOwnerEntityOptions = useMemo(() => {
    return getKGV2BusinessEntityOwnerEntityOptions({
      entityFieldValues,
      entityType,
      kgV2ResolvedPrincipals,
      suggestionID,
      suggestionsByID,
    });
  }, [
    entityFieldValues,
    entityType,
    kgV2ResolvedPrincipals,
    suggestionID,
    suggestionsByID,
  ]);

  const creatableOwnerEntityOptions = useMemo(() => {
    return getKGV2CreatableOwnerEntityOptions({
      entityFieldValues,
      entityType,
      kgV2ResolvedPrincipals,
      suggestionID,
      suggestionsByID,
    });
  }, [
    entityFieldValues,
    entityType,
    kgV2ResolvedPrincipals,
    suggestionID,
    suggestionsByID,
  ]);

  const allowMultiplePrincipals = useMemo(() => {
    return concat(
      ALLOW_MULTIPLE_PRINCIPALS_TYPES_WITH_BUSINESS_ENTITIES
    ).includes(entityType as EntityType);
  }, [entityType]);

  const suggestedPrincipalOptions = useMemo(() => {
    return getKGV2SuggestedPrincipalOptions({
      kgV2ResolvedPrincipals,
      suggestionsByID,
    });
  }, [suggestionsByID, kgV2ResolvedPrincipals]);

  // ------------------------------------------------------------
  // Existing household entities query (reuse cashflow query)
  // ------------------------------------------------------------

  const { data: householdEntitiesData } =
    useHouseholdCashflowEntityOptionsQuery({
      variables: {
        where: { id: householdId },
      },
      skip: !householdId,
    });

  const fetchedExistingEntityOptions = useMemo(() => {
    if (!BUSINESS_ENTITY_TYPES.includes(entityType as EntityType)) {
      return [];
    }

    const firstHousehold = householdEntitiesData?.households?.edges?.[0]?.node;
    if (!firstHousehold) return [];

    const entityNodes = getNodes(firstHousehold.entities);

    return entityNodes
      .filter((ent) => ent.stage !== EntityStage.PreCreated)
      .map((ent) => ({
        display: ent.subtype.displayName,
        value: ent.id,
      }));
  }, [householdEntitiesData, entityType]);

  const existingEntityOptions = useMemo(
    () =>
      excludeExistingEntityOptionsAlreadyIncludedAsOwnerOptions({
        businessEntityOwnerEntityOptions,
        existingEntityOptions: fetchedExistingEntityOptions,
      }),
    [businessEntityOwnerEntityOptions, fetchedExistingEntityOptions]
  );

  return {
    clientProfileOptions,
    suggestedPrincipalOptions,
    businessEntityOwnerEntityOptions,
    creatableOwnerEntityOptions,
    allowMultiplePrincipals,
    existingEntityOptions,
    kgV2ResolvedPrincipals,
  };
}
